// Playground/components/ProblemSceneFrame.jsx

import React from "react";

const styles = {
    card: {
        display: "grid",
        gridTemplateRows: "auto 1px minmax(0, 1fr)",
        height: "100%",
        minHeight: 0,
        border: "1px solid rgba(255, 255, 255, 0.08)",
        borderRadius: "16px",
        background:
            "linear-gradient(180deg, rgba(6, 12, 28, 0.88), rgba(5, 10, 24, 0.82))",
        overflow: "hidden",
        boxShadow: "0 24px 80px rgba(0, 0, 0, 0.28)",
    },
    header: {
        padding: "20px 24px 18px",
        minWidth: 0,
        transition: "padding 160ms ease",
    },
    headerCollapsed: {
        padding: "10px 12px 8px",
    },
    headerTopRow: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) auto",
        alignItems: "start",
        gap: "12px",
        minWidth: 0,
    },
    titleRow: {
        display: "flex",
        alignItems: "center",
        gap: "14px",
        flexWrap: "wrap",
        minWidth: 0,
    },
    title: {
        margin: 0,
        fontSize: "24px",
        fontWeight: 800,
        color: "#f8fafc",
        letterSpacing: "-0.02em",
        lineHeight: 1.15,
        minWidth: 0,
        transition: "font-size 160ms ease, line-height 160ms ease",
    },
    titleCollapsed: {
        fontSize: "17px",
        lineHeight: 1.2,
    },
    collapseButton: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "36px",
        height: "36px",
        padding: 0,
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        background: "rgba(15, 23, 42, 0.72)",
        color: "#dbeafe",
        cursor: "pointer",
        flexShrink: 0,
    },
    collapseIcon: {
        display: "block",
        width: "16px",
        height: "16px",
        transform: "rotate(0deg)",
        transition: "transform 160ms ease",
    },
    collapseIconCollapsed: {
        transform: "rotate(180deg)",
    },
    description: {
        marginTop: "10px",
        color: "#a8b8d8",
        fontSize: "14px",
        lineHeight: 1.55,
        maxWidth: "980px",
    },
    inputPanel: {
        marginTop: "14px",
        minWidth: 0,
    },
    divider: {
        background: "rgba(255, 255, 255, 0.08)",
    },
    canvasRegion: {
        minWidth: 0,
        minHeight: 0,
        padding: "8px",
    },
    canvasCard: {
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 0,
    },
    overlayTopRight: {
        position: "absolute",
        top: "0",
        right: "0",
        zIndex: 3,
    },
    overlayTopLeft: {
        position: "absolute",
        top: "0",
        left: "0",
        zIndex: 3,
    },
    overlayBottomRight: {
        position: "absolute",
        bottom: "0",
        right: "0",
        zIndex: 3,
    },
    overlayBottomLeft: {
        position: "absolute",
        bottom: "0",
        left: "0",
        zIndex: 3,
    },
};

const overlayStyleMap = {
    "top-right": styles.overlayTopRight,
    "top-left": styles.overlayTopLeft,
    "bottom-right": styles.overlayBottomRight,
    "bottom-left": styles.overlayBottomLeft,
};

function ChevronIcon({ isCollapsed }) {
    return (
        <svg
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
                ...styles.collapseIcon,
                ...(isCollapsed ? styles.collapseIconCollapsed : null),
            }}
            aria-hidden="true"
        >
            <path
                d="M3.5 6L8 10.5L12.5 6"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function renderOverlaySlot(position, node) {
    if (!node) {
        return null;
    }

    const overlayStyle = overlayStyleMap[position] || styles.overlayBottomRight;

    return (
        <div key={position} style={overlayStyle}>
            {node}
        </div>
    );
}

export default function ProblemSceneFrame({
    title,
    description,
    headerActions = null,
    inputPanel = null,
    overlay = null,
    overlayPosition = "bottom-right",
    overlaySlots = null,
    children,
    allowHeaderCollapse = true,
    defaultHeaderCollapsed = false,
    isHeaderCollapsed: controlledIsHeaderCollapsed,
    onHeaderCollapsedChange,
}) {
    const isControlled = controlledIsHeaderCollapsed !== undefined;

    const [uncontrolledIsHeaderCollapsed, setUncontrolledIsHeaderCollapsed] =
        React.useState(defaultHeaderCollapsed);

    const isHeaderCollapsed = isControlled
        ? controlledIsHeaderCollapsed
        : uncontrolledIsHeaderCollapsed;

    const setHeaderCollapsed = React.useCallback(
        (nextValue) => {
            if (!isControlled) {
                setUncontrolledIsHeaderCollapsed(nextValue);
            }

            if (typeof onHeaderCollapsedChange === "function") {
                onHeaderCollapsedChange(nextValue);
            }
        },
        [isControlled, onHeaderCollapsedChange]
    );

    const handleToggleHeaderCollapsed = React.useCallback(() => {
        setHeaderCollapsed(!isHeaderCollapsed);
    }, [isHeaderCollapsed, setHeaderCollapsed]);

    const resolvedOverlaySlots = {
        ...(overlay ? { [overlayPosition]: overlay } : null),
        ...(overlaySlots || {}),
    };

    return (
        <div style={styles.card}>
            <div
                style={{
                    ...styles.header,
                    ...(isHeaderCollapsed ? styles.headerCollapsed : null),
                }}
            >
                <div style={styles.headerTopRow}>
                    <div style={styles.titleRow}>
                        <h2
                            style={{
                                ...styles.title,
                                ...(isHeaderCollapsed ? styles.titleCollapsed : null),
                            }}
                        >
                            {title}
                        </h2>
                        {headerActions}
                    </div>

                    {allowHeaderCollapse ? (
                        <button
                            type="button"
                            style={styles.collapseButton}
                            onClick={handleToggleHeaderCollapsed}
                            aria-label={
                                isHeaderCollapsed
                                    ? "Expand header details"
                                    : "Collapse header details"
                            }
                            title={
                                isHeaderCollapsed
                                    ? "Expand header details"
                                    : "Collapse header details"
                            }
                        >
                            <ChevronIcon isCollapsed={isHeaderCollapsed} />
                        </button>
                    ) : null}
                </div>

                {!isHeaderCollapsed ? (
                    <>
                        {description ? (
                            <div style={styles.description}>{description}</div>
                        ) : null}

                        {inputPanel ? (
                            <div style={styles.inputPanel}>{inputPanel}</div>
                        ) : null}
                    </>
                ) : null}
            </div>

            <div style={styles.divider} />

            <div style={styles.canvasRegion}>
                <div style={styles.canvasCard}>
                    {Object.entries(resolvedOverlaySlots).map(([position, node]) =>
                        renderOverlaySlot(position, node)
                    )}
                    {children}
                </div>
            </div>
        </div>
    );
}